import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, PackageCheck, RefreshCw, Repeat2, RotateCcw } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackReturns = [
  {
    _id: "preview-1",
    rmaNumber: "RMA-2209",
    customerName: "Miller Sewing Co.",
    customerEmail: "orders@millersewing.example",
    channel: "facebook_marketplace",
    itemName: "Juki genuine bobbin case",
    sku: "JUKI-1004",
    quantity: 1,
    reason: "wrong_item",
    status: "pending",
    refundAmount: 10,
    notes: [{ text: "Needs photo before approval." }],
  },
];

const tabs = [
  ["All", "all"],
  ["Pending", "pending"],
  ["Approved", "approved"],
  ["Inspection", "inspection"],
  ["Refund Due", "refund_due"],
  ["Exchange", "exchange"],
  ["Rejected", "rejected"],
];

const emptyReturn = {
  customerName: "",
  customerEmail: "",
  channel: "direct",
  itemName: "",
  sku: "",
  quantity: 1,
  reason: "wrong_item",
  refundAmount: 0,
};

function reasonLabel(reason) {
  return String(reason || "other").replaceAll("_", " ");
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function statusTone(status) {
  if (["approved", "refunded", "closed"].includes(status)) return "good";
  if (["rejected"].includes(status)) return "bad";
  if (["refund_due", "exchange", "inspection"].includes(status)) return "info";
  return "warn";
}

export default function Return() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyReturn);
  const [noteText, setNoteText] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const { dismiss, toast, toasts } = useToast();

  const loadReturns = useCallback(async () => {
    const data = await api.get("/returns");
    return data.returns.length ? data.returns : fallbackReturns;
  }, []);

  const { data: returns, error, loading, reload, setData: setReturns } = useAdminResource(loadReturns, fallbackReturns);
  const selectedReturn = returns.find((item) => item._id === selectedId) || returns[0];

  useEffect(() => {
    if (error && !loading) toast(`Using preview returns: ${error}`, "warning");
  }, [error, loading, toast]);

  useEffect(() => {
    setRefundAmount(selectedReturn?.refundAmount || "");
  }, [selectedReturn]);

  const visibleReturns = useMemo(() => {
    return returns.filter((item) => {
      const tabMatches = activeTab === "all" || item.status === activeTab;
      const searchMatches = [
        item.rmaNumber,
        item.customerName,
        item.customerEmail,
        item.itemName,
        item.sku,
        item.reason,
        item.channel,
      ].join(" ").toLowerCase().includes(search.toLowerCase());
      return tabMatches && searchMatches;
    });
  }, [activeTab, returns, search]);
  const returnPagination = usePagination(visibleReturns, [activeTab, search], 10);

  const metrics = {
    open: returns.filter((item) => ["pending", "approved", "inspection", "refund_due", "exchange"].includes(item.status)).length,
    inspection: returns.filter((item) => item.status === "inspection").length,
    refundDue: returns.filter((item) => item.status === "refund_due").length,
    exchanges: returns.filter((item) => item.status === "exchange").length,
  };

  const reasonCounts = useMemo(() => {
    return returns.reduce((acc, item) => {
      acc[item.reason] = (acc[item.reason] || 0) + 1;
      return acc;
    }, {});
  }, [returns]);

  const updateCreateForm = (event) => {
    setCreateForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const replaceReturn = (nextReturn) => {
    setReturns((current) => current.map((item) => item._id === nextReturn._id ? nextReturn : item));
  };

  const createReturn = async (event) => {
    event.preventDefault();
    try {
      const data = await api.post("/returns", {
        ...createForm,
        quantity: Number(createForm.quantity || 1),
        refundAmount: Number(createForm.refundAmount || 0),
      });
      setReturns((current) => [data.returnRequest, ...current.filter((item) => !String(item._id).startsWith("preview"))]);
      setSelectedId(data.returnRequest._id);
      setCreateForm(emptyReturn);
      setCreateOpen(false);
      toast(`Created ${data.returnRequest.rmaNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const action = async (path, successMessage, body, method = "post") => {
    if (!selectedReturn?._id || String(selectedReturn._id).startsWith("preview")) return;
    try {
      const data = method === "patch"
        ? await api.patch(`/returns/${selectedReturn._id}/${path}`, body || {})
        : await api.post(`/returns/${selectedReturn._id}/${path}`, body || {});
      replaceReturn(data.returnRequest);
      toast(successMessage, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const updateReturnStatus = async (status) => {
    if (!selectedReturn?._id || String(selectedReturn._id).startsWith("preview")) return;
    try {
      const data = await api.patch(`/returns/${selectedReturn._id}`, { status });
      replaceReturn(data.returnRequest);
      toast(`Moved ${data.returnRequest.rmaNumber} to ${reasonLabel(status)}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await action("notes", "Note added", { text: noteText.trim() });
    setNoteText("");
  };

  const processRefund = async () => {
    await action("refund", "Refund marked processed", { refundAmount: Number(refundAmount || 0) });
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">RMA Desk</p>
          <h1 className="text-3xl font-black text-gray-900">Returns</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Review customer return requests, approve labels, inspect received goods, and manually process refunds minus shipping cost.
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: createOpen ? "Close Return Form" : "Create Return", primary: true, onClick: () => setCreateOpen((open) => !open) },
            { label: "Approve Return", onClick: () => action("approve", "Return approved", {}, "patch") },
            { label: "Reject Return", onClick: () => action("reject", "Return rejected", {}, "patch") },
            { label: "Refresh", onClick: reload },
          ]}
        />
      </div>

      {createOpen && (
        <form className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm" onSubmit={createReturn}>
          <h2 className="mb-4 text-lg font-black text-gray-900">Create Return Request</h2>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["customerName", "Customer name"], ["customerEmail", "Customer email"], ["itemName", "Item name"], ["sku", "SKU"],
              ["quantity", "Qty"], ["refundAmount", "Refund amount"],
            ].map(([name, label]) => (
              <label className="block" key={name}>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name={name} onChange={updateCreateForm} required={!["sku", "refundAmount"].includes(name)} type={["quantity", "refundAmount"].includes(name) ? "number" : "text"} value={createForm[name]} />
              </label>
            ))}
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Reason</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="reason" onChange={updateCreateForm} value={createForm.reason}>
                <option value="wrong_item">Wrong item</option><option value="damaged">Damaged</option><option value="defective">Defective</option><option value="no_longer_needed">No longer needed</option><option value="exchange">Exchange</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Channel</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="channel" onChange={updateCreateForm} value={createForm.channel}>
                <option value="direct">Direct</option><option value="facebook_marketplace">Facebook market place</option><option value="ebay">eBay</option><option value="bobbinusa">Bobbin USA</option>
              </select>
            </label>
          </div>
          <button className="mt-5 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-bold text-white" type="submit">Save Return</button>
        </form>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard title="Open Returns" value={metrics.open} note="Requests awaiting action" />
        <InsightCard title="In Inspection" value={metrics.inspection} note="Received items being checked" />
        <InsightCard title="Refunds Due" value={metrics.refundDue} note="Approved refunds pending payment" tone="warn" />
        <InsightCard title="Exchanges" value={metrics.exchanges} note="Replacement part workflows" tone="good" />
      </div>

      <section className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] lg:max-w-md" onChange={(event) => setSearch(event.target.value)} placeholder="Search RMA, customer, SKU, reason..." value={search} />
          <div className="flex flex-wrap gap-2">
            {tabs.map(([label, status]) => (
              <button className={`rounded-full px-4 py-2 text-xs font-black ${activeTab === status ? "bg-[#4391d1] text-white" : "border border-gray-200 bg-white text-gray-600"}`} key={status} onClick={() => setActiveTab(status)} type="button">{label}</button>
            ))}
          </div>
        </div>
      </section>

      <div className="grid gap-6 2xl:grid-cols-[1fr_430px]">
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
            <h2 className="text-lg font-black text-gray-900">Return Requests</h2>
            <span className="text-xs font-bold text-gray-400">{visibleReturns.length} visible</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                <tr>{["RMA", "Customer", "Item", "Reason", "Channel", "Refund", "Status", "Label"].map((column) => <th className="whitespace-nowrap px-6 py-4 font-black" key={column}>{column}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {returnPagination.paginatedItems.map((item) => (
                  <tr className={`cursor-pointer text-sm text-gray-600 ${selectedReturn?._id === item._id ? "bg-blue-50/50" : ""}`} key={item._id} onClick={() => setSelectedId(item._id)}>
                    <td className="px-6 py-4 font-black text-gray-900">{item.rmaNumber}</td>
                    <td className="min-w-52 px-6 py-4"><div className="font-bold text-gray-900">{item.customerName}</div><div className="text-xs text-gray-400">{item.customerEmail}</div></td>
                    <td className="min-w-56 px-6 py-4"><div className="font-bold text-gray-900">{item.itemName}</div><div className="text-xs text-gray-400">SKU {item.sku || "N/A"} · Qty {item.quantity}</div></td>
                    <td className="px-6 py-4 capitalize">{reasonLabel(item.reason)}</td>
                    <td className="px-6 py-4 capitalize">{item.channel}</td>
                    <td className="px-6 py-4 font-bold">{money(item.refundAmount)}</td>
                    <td className="px-6 py-4"><StatusPill tone={statusTone(item.status)}>{reasonLabel(item.status)}</StatusPill>{item.requestedByCustomer && <p className="mt-1 text-[10px] font-black uppercase text-[#1278b5]">Customer submitted</p>}</td>
                    <td className="px-6 py-4">{item.returnLabel?.trackingNumber ? <StatusPill tone="good">Generated</StatusPill> : <StatusPill>Needed</StatusPill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={returnPagination} />
        </section>

        {selectedReturn && (
          <aside className="space-y-5">
            <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div><h2 className="text-lg font-black text-gray-900">{selectedReturn.rmaNumber}</h2><p className="mt-1 text-xs font-bold text-gray-400">{selectedReturn.customerName} · {selectedReturn.customerEmail}</p></div>
                <StatusPill tone={statusTone(selectedReturn.status)}>{reasonLabel(selectedReturn.status)}</StatusPill>
              </div>
              <div className="mt-5 rounded-xl bg-blue-50 p-4 text-sm font-semibold leading-6 text-blue-900">
                <p className="font-black">Policy check</p>
                <p>14-day window: {selectedReturn.eligibilityStatus ? reasonLabel(selectedReturn.eligibilityStatus) : "needs review"}</p>
                <p>Original package confirmed: {selectedReturn.originalPackagingConfirmed ? "Yes" : "No"}</p>
                <p>Refunds are admin-only after item inspection. Deduct shipping from final refund.</p>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <button className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white" onClick={() => action("approve", "Return approved", {}, "patch")} type="button"><PackageCheck className="mr-2 inline h-4 w-4" />Approve</button>
                <button className="rounded-xl bg-red-600 px-4 py-3 text-sm font-bold text-white" onClick={() => action("reject", "Return rejected", {}, "patch")} type="button">Reject</button>
                <button className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-bold text-white" onClick={() => action("label", "Return label generated", { carrier: "UPS" })} type="button"><FileText className="mr-2 inline h-4 w-4" />Generate Label</button>
                <button className="rounded-xl bg-gray-950 px-4 py-3 text-sm font-bold text-white" onClick={() => updateReturnStatus("inspection")} type="button"><RefreshCw className="mr-2 inline h-4 w-4" />Inspection</button>
              </div>
            </section>

            <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 font-black text-gray-900"><RotateCcw size={16} /> Admin Refund Processing</div>
              <p className="mb-3 text-xs font-bold leading-5 text-gray-500">
                Process only after goods are received in perfect condition. Enter the final refund amount after deducting shipping cost.
              </p>
              <div className="flex gap-2">
                <input className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold" onChange={(event) => setRefundAmount(event.target.value)} type="number" value={refundAmount} />
                <button className="rounded-xl bg-gray-950 px-4 py-2 text-sm font-bold text-white" onClick={processRefund} type="button">Mark Refunded</button>
              </div>
              <button className="mt-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-800" onClick={() => updateReturnStatus("refund_due")} type="button">Move to Refund Due</button>
            </section>

            <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2 font-black text-gray-900"><Repeat2 size={16} /> Exchange Handling</div>
              <p className="text-sm text-gray-500">Replacement SKU: {selectedReturn.exchange?.replacementSku || "Not assigned"}</p>
              <button className="mt-3 w-full rounded-xl bg-blue-50 px-4 py-3 text-sm font-bold text-[#1278b5]" onClick={() => updateReturnStatus("exchange")} type="button">Move to Exchange</button>
            </section>

            <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 font-black text-gray-900">Notes / Comments</div>
              <div className="space-y-2">
                {(selectedReturn.notes || []).map((note, index) => <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600" key={`${note.text}-${index}`}>{note.text}</div>)}
              </div>
              <textarea className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold" onChange={(event) => setNoteText(event.target.value)} placeholder="Add internal note..." rows="3" value={noteText} />
              <button className="mt-3 w-full rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-bold text-white" onClick={addNote} type="button">Add Note</button>
            </section>
          </aside>
        )}
      </div>

      <section className="mt-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-black text-gray-900">Return Reasons Analytics</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(reasonCounts).map(([reason, count]) => <StatusPill key={reason} tone="info">{reasonLabel(reason)}: {count}</StatusPill>)}
        </div>
      </section>
    </div>
  );
}
